/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeAgFormulaBar.tsx
 * FILE_ID: QB-AGGRID-FORMULA-BAR-001
 * PURPOSE: Excel-style formula bar above the AG-Grid (row label, value
 *          input, hints, delete-focused-row button).
 * WHY: Extracted from ScopeAgGridSection.tsx so that file stays under the
 *      300-line cap (rule 1.7) after the SAM-style trailing-empties wiring
 *      added the pad-promotion handlers.
 * FLOW:
 *   ┌──────────────────┐   ┌──────────────────┐   ┌──────────────────┐
 *   │ focusedRow,      │ → │ render row tag + │ → │ commit on input  │
 *   │ formulaCellValue │   │ value input      │   │ change           │
 *   └──────────────────┘   └──────────────────┘   └──────────────────┘
 * RELATED:
 *   - ScopeAgGridSection (QB-AGGRID-SECTION-001) — consumer
 */

import { Trash2 } from 'lucide-react'
import type { QuoteLineItem } from '@/lib/quoteApi'

export interface ScopeAgFormulaBarProps {
  focusedRow: QuoteLineItem | null
  field: string | null
  value: string
  rowIndex: number | null
  isEditable: boolean
  onChange: (next: string) => void
  onDeleteFocused: () => void
}

export default function ScopeAgFormulaBar({
  focusedRow,
  field,
  value,
  rowIndex,
  isEditable,
  onChange,
  onDeleteFocused,
}: ScopeAgFormulaBarProps) {
  const hasFocus = Boolean(focusedRow && field)
  const rowLabel = focusedRow
    ? `Row ${focusedRow.item_number ?? (rowIndex !== null ? rowIndex + 1 : '?')} · ${field}`
    : 'No cell selected'

  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-2 py-1.5 text-xs">
      <span className="font-mono text-slate-500 dark:text-slate-400 min-w-[140px]">
        {rowLabel}
      </span>
      <div className="mx-1 h-3 w-px bg-slate-300 dark:bg-slate-600" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!isEditable || !hasFocus}
        placeholder={
          hasFocus ? 'Enter value or =formula…' : 'Click a cell to edit'
        }
        className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
      />
      <span className="text-[10px] text-slate-500 dark:text-slate-400 hidden md:block">
        Tab/Enter/Arrows · Ctrl+D fill · paste from Excel
      </span>
      {hasFocus && isEditable && (
        <button
          onClick={onDeleteFocused}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
          title="Delete focused row"
        >
          <Trash2 className="w-3 h-3" />
          Delete row
        </button>
      )}
    </div>
  )
}
